import * as DateFns from "date-fns"

const useDateTime = () => {

	/**
	 * Check if the given date is before today's date
	 * @param {Date} date The given date to compare with the start of today's date
	 * @returns {boolean} Returns true if the given date is today
	 */
	const isBeforeToday = (date) => DateFns.isBefore(date, DateFns.startOfDay(new Date()))

	/**
	 * Check if the date is before the given date
	 * @param {Date} date The given date to compare with the start of the given date
	 * @param {Date} givenDate The given date to compare with
	 * @returns {boolean} Returns true if the given date is before the given date
	 */
	const isBeforeGivenDate = (date, givenDate) => DateFns.isBefore(date, DateFns.startOfDay(givenDate))

	/**
	 * Check if the date is after the given date
	 * @param {Date} date The given date to compare with the end of the given date
	 * @param {Date} givenDate The given date to compare with
	 * @returns {boolean} Returns true if the given date is after the given date
	 */
	const isAfterGivenDate = (date, givenDate) => DateFns.isAfter(date, DateFns.endOfDay(givenDate))

	/**
	 * Check if the given date range is natural (start date is before end date)
	 * @param {Date} startDate The start date of the range
	 * @param {Date} endDate The end date of the range
	 * @returns {boolean} Returns true if the start date is before the end date
	 */
	const isValidDateRange = (startDate, endDate) => DateFns.isBefore(startDate, endDate)

	/**
	 * Compare two time values without considering the date part
	 * @param {Date} d1 The first dateTime value
	 * @param {Date} d2 The second dateTime value
	 * @param {Function|(t1: number, t2: number, unit: string) => boolean} rules The comparison rules for each time unit
	 * 
	 * @returns {boolean} Returns true if t1 is before t2, false if t1 is after t2
	 */
	const isValidTimeRange = (d1, d2, rules) => {
		const h1 = DateFns.getHours(d1), h2 = DateFns.getHours(d2)
		if (h1 !== h2) return rules(h1, h2, "hour")

		const m1 = DateFns.getMinutes(d1), m2 = DateFns.getMinutes(d2)
		if (m1 !== m2) return rules(m1, m2, "minute")

		const s1 = DateFns.getSeconds(d1), s2 = DateFns.getSeconds(d2)
		if (s1 !== s2) return rules(s1, s2, "second")

		const ms1 = DateFns.getMilliseconds(d1), ms2 = DateFns.getMilliseconds(d2)
		if (ms1 !== ms2) return rules(ms1, ms2, "millisecond")

		return false
	}

	return { isBeforeToday, isBeforeGivenDate, isAfterGivenDate, isValidDateRange, isValidTimeRange }
}

export default useDateTime
